import apiClient from '../apiClient';
import type { ApiResponse, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/admin/wallets';

export interface AdminWallet {
  id: string;
  userId: string;
  companyId: string;
  balance: number;
  reservedAmount: number;
  currency: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    role: string;
    companyId?: string;
    status: string;
    company?: { id: string; name: string };
  };
}

export interface WalletTransactionItem {
  id: string;
  walletId: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  type: string;
  referenceType?: string;
  referenceId?: string;
  description?: string;
  status: string;
  createdAt: string;
}

interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMeta;
}

// API Functions

export const getWallets = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string,
  userId?: string
): Promise<PaginatedResponse<AdminWallet>> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page, limit, search, sortBy, sortOrder, status, userId,
  });
  const { data } = await apiClient.get<ApiResponse<AdminWallet[]>>(url);
  return { items: data.data, pagination: data.meta! };
};

export const getUserWallets = async (userId: string): Promise<AdminWallet[]> => {
  const { items } = await getWallets(1, 100, undefined, undefined, undefined, undefined, userId);
  return items;
};

export const getWalletTransactions = async (
  userId: string,
  page?: number,
  limit?: number
): Promise<PaginatedResponse<WalletTransactionItem>> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/${userId}/transactions`, { page, limit });
  const { data } = await apiClient.get<ApiResponse<WalletTransactionItem[]>>(url);
  return { items: data.data, pagination: data.meta! };
};

export const adjustWalletBalance = async (
  userId: string,
  body: { amount: number; type: 'credit' | 'debit'; reason: string; companyId: string }
): Promise<any> => {
  const { data } = await apiClient.post(`${BASE_URL}/${userId}/adjust`, body);
  return data.data;
};

// React Query Hooks

export const useGetWallets = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_WALLETS, page, limit, search, sortBy, sortOrder, status],
    queryFn: () => getWallets(page, limit, search, sortBy, sortOrder, status),
  });

export const useGetUserWallets = (userId: string | undefined) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_WALLETS, 'user', userId],
    queryFn: () => getUserWallets(userId!),
    enabled: !!userId,
  });

export const useGetWalletTransactions = (
  userId: string,
  page?: number,
  limit?: number
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_WALLET_TRANSACTIONS, userId, page, limit],
    queryFn: () => getWalletTransactions(userId, page, limit),
    enabled: !!userId,
  });

export const useAdjustWalletBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: string; amount: number; type: 'credit' | 'debit'; reason: string; companyId: string }) =>
      adjustWalletBalance(userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_WALLETS] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_WALLET_TRANSACTIONS] });
    },
  });
};
